import { merge } from '../../helper';

import type { IElementConfig } from '../../utils/element/types';

import appendChildren from '../../general/append/appendChildren';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';
import isObject from '../../general/isObject';
import prependChild from '../../general/prepend/prependChild';

import * as exInput from '../input';
import * as exValidation from '../validation';

import * as exElement from '../../utils/element';

// ----------------------------------------------------------------------------

export interface IFormGroupConfig extends IElementConfig {
    label?: Record<string, any> | string;
    element?: Record<string, any>;
    input?: Record<string, any>;
    inputGroup?: Record<string, any>;
    validation?: Record<string, any>;
}

// ----------------------------------------------------------------------------

function create(config: IFormGroupConfig = {}): Record<string, any> {
    // Default Config
    const defConfig: Record<string, any> = {};

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const elements: Record<string, any> = {};

    // Create element (form group)
    const elFormGroup = exElement.create({
        merge: defConfig,
        class: {
            new: 'form-group',
        },
    });
    elements.element = elFormGroup;

    // ------------------------------------------------------------------------

    // Settings
    settings(elements, defConfig);

    // ------------------------------------------------------------------------

    // Return
    return elements;
}

function settings(elements: Record<string, any>, config: IFormGroupConfig = {}): void {
    // If using label
    if (config.label != null) {
        // If label is string
        if (typeof config.label === 'string') {
            config.label = {
                html: config.label,
            };
        }

        // Create element (label)
        const elLabel = exElement.create({
            merge: config.label,
            tag: 'label',
            class: {
                new: 'form-label',
            },
        });
        prependChild(elements.element, elLabel);
        elements.label = elLabel;
    }

    // ------------------------------------------------------------------------

    // If using element
    if (config.element != null) {
        const arr = Array.isArray(config.element) ? config.element : [config.element];
        appendChildren(elements.element, arr);
    }

    // If using input
    if (config.input) {
        //
        if (isObject(config.input)) {
            // Create element (input)
            const input = exInput.create({
                merge: config.input,
            });
            elements.element.appendChild(input.element);
            elements.input = input.element;
        }
        //
        else {
            const arr = Array.isArray(config.input) ? config.input : [config.input];
            appendChildren(elements.element, arr);
        }

        // If using validation
        if (!isEmptyObject(config.validation)) {
            // Create element (validation)
            const validation = exValidation.create({
                merge: config.validation,
            });
            appendChildren(elements.element, validation.list);
            elements.validation = validation.list;

            // If using validation tooltip
            if (config.validation?.tooltip === true) {
                elements.element.style.position = 'relative';
            }
        }
    }

    // If using input group
    if (config.inputGroup) {
        //
        if (isObject(config.inputGroup)) {
            // Create element (input group)
            const inputGroup = exInput.group.create({
                merge: config.inputGroup,
            });
            elements.element.appendChild(inputGroup.element);
            elements.inputGroup = inputGroup;
            elements.input = inputGroup.input;

            // If using validation
            if (!isEmptyObject(config.validation)) {
                // Create element (validation)
                const validation = exValidation.create({
                    merge: config.validation,
                });
                appendChildren(inputGroup.element, validation.list);
                elements.validation = validation.list;

                // If using validation tooltip
                if (config.validation?.tooltip === true) {
                    elements.element.style.position = 'relative';
                }
            }
        }
        //
        else {
            const arr = Array.isArray(config.inputGroup) ? config.inputGroup : [config.inputGroup];
            appendChildren(elements.element, arr);
        }
    }
}

// ----------------------------------------------------------------------------

export { create, settings };
